import { signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { QueryFunctionContext } from '@tanstack/react-query';
import { compact, filter, find, flatMap, map, uniq } from 'es-toolkit/compat';
import pLimit from 'p-limit';
import { useMemo } from 'react';

import { getPreSignedPresentationInfo } from 'api/experimentResults';
import { getPrimaryRunOrchestrationIdFromExperimentResult } from 'components/Procedure/useSlideChannelsAndResults/secondaryAnalysis';
import { Slide } from 'interfaces/slide';
import { useQueriesWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';

export const experimentResultsRequiringPreSigning = signal<number[]>([]);

const limit = pLimit(5); // Limit to 5 concurrent requests - to avoid overwhelming the server or browser
const limitedGetPreSignedPresentationInfo = (studyId: string, experimentResultId: number, abortSignal: AbortSignal) => {
  return limit(() => getPreSignedPresentationInfo(studyId, experimentResultId, abortSignal));
};

export const usePreSignPresentationInfoByExperimentResultIds = ({
  studyId,
  procedureSlides,
  enabled = true,
  debug = false,
}: {
  studyId: string;
  procedureSlides: Slide[];
  enabled?: boolean;
  debug?: boolean;
}) => {
  useSignals();
  const signalExperimentResults = experimentResultsRequiringPreSigning.value;
  const effectiveExperimentResultsRequiringPreSigning = useMemo(() => {
    if (debug) {
      console.debug(
        'usePreSignPresentationInfoByExperimentResultIds - Recalculating effectiveExperimentResultsRequiringPreSigning',
        {
          signalExperimentResults,
          procedureSlides,
        }
      );
    }
    const allExperimentResults = flatMap(procedureSlides, (slide) => slide.experimentResults || []);
    const publishedExperimentResultIds = compact(map(filter(allExperimentResults, 'approved'), 'experimentResultId'));
    const selectedExperimentResultIds = new Set(compact(map(allExperimentResults, 'experimentResultId')));
    const experimentResultIdsIncludingSecondaryAnalysis = uniq([
      ...filter(signalExperimentResults, (experimentResultId) => selectedExperimentResultIds.has(experimentResultId)),
      ...publishedExperimentResultIds,
    ]);
    return uniq(
      map(experimentResultIdsIncludingSecondaryAnalysis, (id) => {
        const experimentResult = find(allExperimentResults, { experimentResultId: id });
        const primaryRunOrchestrationId = getPrimaryRunOrchestrationIdFromExperimentResult(experimentResult);
        const primaryRunExperimentResultId =
          primaryRunOrchestrationId &&
          find(allExperimentResults, {
            orchestrationId: primaryRunOrchestrationId,
            slideId: experimentResult?.slideId,
            studyId: experimentResult?.studyId,
          })?.experimentResultId;
        return primaryRunExperimentResultId || id;
      })
    );
  }, [procedureSlides, signalExperimentResults]);
  return useQueriesWithErrorAndRetrySnackbar<{ [experimentResultId: number]: { [key: string]: any } }>({
    queries:
      Boolean(studyId) && enabled
        ? map(effectiveExperimentResultsRequiringPreSigning, (experimentResultId) => ({
            queriedEntityName: 'heatmap data',
            queryKey: ['getPreSignedExperimentResultPresentationInfo', { studyId, experimentResultId }],
            queryFn: ({ signal: abortSignal }: QueryFunctionContext) =>
              limitedGetPreSignedPresentationInfo(studyId, experimentResultId, abortSignal),
          }))
        : [],
  });
};
